import { Plus, CalendarX } from 'lucide-react';

import type { ScheduleEvent } from '../../../services/schedule';

import { ClassCard } from './ClassCard';
import {
  DAY_NAMES,
  isToday,
  startOfWeek,
  toDateKey,
} from './scheduleDateUtils';

interface WeekCalendarProps {
  anchor: Date;
  eventsByDate: Record<string, ScheduleEvent[]>;
  enrolledClassIds: Set<string>;
  checkedInClassIds: Set<string>;
  isOwner: boolean;
  isLoading: boolean;
  onEventClick: (event: ScheduleEvent) => void;
  onCreateClass?: (date: string) => void;
}

function getWeekDates(anchor: Date): Date[] {
  const start = startOfWeek(anchor);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });
}

export function WeekCalendar({
  anchor,
  eventsByDate,
  enrolledClassIds,
  checkedInClassIds,
  isOwner,
  isLoading,
  onEventClick,
  onCreateClass,
}: WeekCalendarProps) {
  const days = getWeekDates(anchor);
  const totalEvents = days.reduce(
    (sum, d) => sum + (eventsByDate[toDateKey(d)]?.length ?? 0),
    0
  );

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Desktop: 7-column grid */}
      <div className="hidden md:grid md:grid-cols-7 gap-px bg-[#363232] rounded-xl overflow-hidden flex-1 min-h-0">
        {days.map((day) => {
          const key = toDateKey(day);
          const dayEvents = eventsByDate[key] ?? [];
          const today = isToday(day);

          return (
            <div key={key} className="bg-[#1A1717] flex flex-col min-h-0">
              {/* Day header */}
              <div
                className={`flex items-center justify-between px-2 py-2 border-b border-[#363232] shrink-0 ${
                  today ? 'bg-[#9B1421]/10' : ''
                }`}
              >
                <div>
                  <p className="text-[#A9A3A3] text-[10px] uppercase font-medium">
                    {DAY_NAMES[day.getDay()]}
                  </p>
                  <p
                    className={`text-sm font-bold leading-none mt-0.5 ${
                      today ? 'text-[#9B1421]' : 'text-[#EBFFEE]'
                    }`}
                  >
                    {day.getDate()}
                  </p>
                </div>
                {isOwner && onCreateClass && (
                  <button
                    onClick={() => onCreateClass(key)}
                    className="p-1 text-[#504A4A] hover:text-[#9B1421] transition-colors rounded"
                    aria-label="Create class on this day"
                  >
                    <Plus size={12} />
                  </button>
                )}
              </div>

              {/* Events */}
              <div className="flex-1 overflow-y-auto p-1.5 space-y-1.5 min-h-[80px]">
                {isLoading && dayEvents.length === 0 ? (
                  <div className="h-6 bg-[#2A2626] rounded animate-pulse" />
                ) : (
                  dayEvents.map((event) => (
                    <ClassCard
                      key={event.event_id}
                      event={event}
                      isEnrolled={enrolledClassIds.has(event.event_id)}
                      isCheckedIn={checkedInClassIds.has(event.event_id)}
                      onClick={() => onEventClick(event)}
                      view="week"
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile: single-day list view */}
      <div className="md:hidden flex-1 overflow-y-auto">
        {days.map((day) => {
          const key = toDateKey(day);
          const dayEvents = eventsByDate[key] ?? [];
          const today = isToday(day);

          if (dayEvents.length === 0) return null;

          return (
            <div key={key} className="mb-4">
              <div className="flex items-center gap-2 px-1 mb-2">
                <span
                  className={`text-xs font-semibold uppercase ${
                    today ? 'text-[#9B1421]' : 'text-[#A9A3A3]'
                  }`}
                >
                  {DAY_NAMES[day.getDay()]}
                </span>
                <span
                  className={`text-sm font-bold ${
                    today ? 'text-[#9B1421]' : 'text-[#EBFFEE]'
                  }`}
                >
                  {day.toLocaleDateString([], { month: 'short', day: 'numeric' })}
                </span>
                {today && (
                  <span className="text-[10px] bg-[#9B1421] text-white px-1.5 py-0.5 rounded-full font-medium">
                    Today
                  </span>
                )}
              </div>
              <div className="space-y-2">
                {dayEvents.map((event) => (
                  <ClassCard
                    key={event.event_id}
                    event={event}
                    isEnrolled={enrolledClassIds.has(event.event_id)}
                    isCheckedIn={checkedInClassIds.has(event.event_id)}
                    onClick={() => onEventClick(event)}
                    view="day"
                  />
                ))}
              </div>
            </div>
          );
        })}

        {!isLoading && totalEvents === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <CalendarX size={36} className="text-[#504A4A] mb-3" />
            <p className="text-[#A9A3A3] text-sm">No classes this week.</p>
            {isOwner && onCreateClass && (
              <button
                onClick={() => onCreateClass(toDateKey(new Date()))}
                className="mt-3 text-sm text-[#9B1421] hover:underline"
              >
                + Create a class
              </button>
            )}
          </div>
        )}
      </div>

      {/* Desktop empty state */}
      {!isLoading && totalEvents === 0 && (
        <div className="hidden md:flex flex-col items-center justify-center py-12 text-center">
          <CalendarX size={36} className="text-[#504A4A] mb-3" />
          <p className="text-[#A9A3A3] text-sm">No classes scheduled this week.</p>
          {isOwner && onCreateClass && (
            <button
              onClick={() => onCreateClass(toDateKey(new Date()))}
              className="mt-3 text-sm text-[#9B1421] hover:underline"
            >
              + Create a class
            </button>
          )}
        </div>
      )}
    </div>
  );
}
